import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {IoIosRefresh} from "react-icons/io";
import API, {cleanPicturePath} from "../../context/API";
import SearchBar from "../../theme/Layout/Searchbar";

function SoldOutOptions({tabIndex, role }) {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false); // Separate state for refresh loading
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null); // To store last updated time
  const [searchQuery, setSearchQuery] = useState("");

  const bgColor = useColorModeValue("white", "gray.800");

  const { isOpen, onClose } = useDisclosure();
  const toast = useToast();
  const sortByUpdatedAt = (data) => {
    if (!Array.isArray(data)) {
      throw new Error("Input data must be an array.");
    }
    return [...data].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  };

  const fetchSoldOutItems = useCallback(async () => {
    try {
      setRefreshing(true);
      const response = await API.get(
        "options/stock?type=Out Of Stock&page=1&limit=100"
      );
      const itemsData = sortByUpdatedAt(response.data.data?.result || []);
      if (response.data.success) {
        setItems(itemsData);
        setFilteredItems(itemsData);
        setLastUpdated(new Date().toLocaleString());
      } else {
        setError("Failed to fetch sold-out items.");
      }
    } catch (err) {
      setError("An error occurred while fetching sold-out items.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (tabIndex === 7) {
      fetchSoldOutItems();
    }
  }, [tabIndex, fetchSoldOutItems]);

  const handleToggleSoldOut = async (item) => {
    try {
      const updatedItem = { ...item, sold_out: false };
      updatedItem.picture = cleanPicturePath(updatedItem.picture);

      // Process category_names to ensure it's an array of IDs as strings
      if (Array.isArray(updatedItem.category_names)) {
        updatedItem.category_names = updatedItem.category_names.map(
          (category) => {
            if (
              typeof category === "object" &&
              category !== null &&
              "id" in category
            ) {
              return category.id.toString();
            }
            return category.toString();
          }
        );
      }

      // Process customizations to ensure it's an array of IDs as strings
      if (Array.isArray(updatedItem.customizations)) {
        updatedItem.customizations = updatedItem.customizations.map(
          (customization) => {
            if (
              typeof customization === "object" &&
              customization !== null &&
              "id" in customization
            ) {
              return customization.id.toString();
            }
            return customization.toString();
          }
        );
      }

      // Create a clean payload without server-managed fields
      const cleanPayload = {
        option: updatedItem.option,
        description: updatedItem.description,
        price: updatedItem.price,
        sequence_number: updatedItem.sequence_number,
        sold_out: updatedItem.sold_out,
        customization: updatedItem.customization
      };


      await API.put(`/options/${item.id}`, cleanPayload);

      toast({
        title: "Option status updated.",
        description: "The Option is now available.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Refresh the list after toggling
      await fetchSoldOutItems();
      onClose();
    } catch (error) {
      console.error("Error updating item:", error);
      toast({
        title: "An error occurred.",
        description: "Unable to update the item.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const confirmToggleSoldOut = (item) => {
    // onOpen();
    handleToggleSoldOut(item)
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    const results = items.filter((item) =>
      item.item.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredItems(results);
  };

  const RefreshButton = () => (
    <IconButton
      icon={<IoIosRefresh />}
      size={"sm"}
      onClick={fetchSoldOutItems}
      isLoading={refreshing}
      loadingText="Refreshing..."
      mx={4}
      px={2}
    />
  );

  if (loading && !refreshing) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Text color="red.500">{error}</Text>
      </Box>
    );
  }

  return (
    <Box my={"4"}>
      <Flex
        justify={{ base: "flex-start", md: "space-between" }}
        //align="center"
        marginBottom="4"
        direction={{ base: "column", md: "row" }}
      >
        <Box>
          <Heading size={"xl"} letterSpacing={-2}>
            Sold out customizations options
          </Heading>
          <Text fontSize={"sm"}>
            List of all sold out customization options, you can mark new ones as sold out, they
            will appear here.
          </Text>
        </Box>
        <Flex align="center">
          <Text fontSize="sm" color="gray.500">
            Last updated: {lastUpdated || "Never"}
          </Text>
          <RefreshButton />
        </Flex>
      </Flex>
      <SearchBar
        items={items.map((item) => item.item)}
        onSearch={handleSearch}
        placeholder="Search items..."
        initialValue={searchQuery}
        variant="filled"
        borderRadius="full"
        width="100%"
      />
      <Box p={4} bg={bgColor} borderRadius="md" overflowX="auto">
        <Table variant="simple" borderRadius="md" mt="4" minWidth="600px">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Description</Th>
              {/* <Th>Price</Th> */}
              <Th textAlign={"center"}>Sold out</Th>
            </Tr>
          </Thead>
          <Tbody>
            {refreshing ? (
              <Tr>
                <Td colSpan="5">
                  <Flex
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <Spinner size="lg" />
                  </Flex>
                </Td>
              </Tr>
            ) : (
              filteredItems.map((item) => (
                <Tr key={item.id}>
                  <Td>{item.option}</Td>
                  <Td>{item.description || "No description available"}</Td>
                  {/* <Td>
                    $
                    {item.price !== null && item.price !== undefined
                      ? (item.price || 0).toFixed(2)
                      : "Free"}
                  </Td> */}
                  <Td>
                    <Flex justifyContent={"center"}>
                      <Switch
                        isChecked={item.sold_out}
                        onChange={() => confirmToggleSoldOut(item)}
                      />
                    </Flex>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>
      {/* Toggle Sold-Out Status Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Status Change</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to mark this item as available again?
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="green" mr={3} onClick={handleToggleSoldOut}>
              Yes, Make Available
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
export default SoldOutOptions;
